import React from "react";
import { useNavigate } from "react-router-dom";

import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { CircularProgress } from "@mui/material";

import { tokenRequest, urlConfig } from "../../../../config";
import { initializeOpenSession } from "../../../../services/api";
import { decodeToken } from "../../../../utils/helper";

export const PayLinkLoginComplete = () => {
  const { instance } = useMsal();
  const navigate = useNavigate();
  const [isSessionOpen, setIsSessionOpen] = React.useState(false);
  const [decodedToken, setDecodedToken] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [statusMessage, setStatusMessage] = React.useState(
    "Signing you in, please wait...",
  );

  React.useEffect(() => {
    const handleLoginComplete = async () => {
      try {
        const redirectResponse = await instance.handleRedirectPromise();

        if (redirectResponse && redirectResponse.accessToken) {
          console.log("Got token from redirect response");
          const decoded = decodeToken(redirectResponse.accessToken);
          setDecodedToken(decoded);
          setStatusMessage("Preparing your session...");
          await handleOpenSession(decoded);
          return;
        }

        // Fallback: no redirect response, check if already signed in
        const allAccounts = instance.getAllAccounts();
        if (allAccounts.length > 0) {
          const account = allAccounts[0];
          console.log(
            "No redirect response, trying silent token acquisition...",
          );

          try {
            const response = await instance.acquireTokenSilent({
              ...tokenRequest,
              account: account,
              authority: urlConfig.B2C_PAYLINK_PASSWORDLESS_AUTHORITY,
            } as any);

            if (response) {
              const decoded = decodeToken(response.accessToken);
              setDecodedToken(decoded);
              setStatusMessage("Preparing your session...");
              await handleOpenSession(decoded);
            }
          } catch (err) {
            if (err instanceof InteractionRequiredAuthError) {
              await instance.acquireTokenRedirect({
                ...tokenRequest,
                account: account,
                authority: urlConfig.B2C_PAYLINK_PASSWORDLESS_AUTHORITY,
              } as any);
            } else {
              console.error("Token acquisition failed:", err);
              setIsLoading(false);
            }
          }
        } else {
          console.log("No accounts found after redirect");
          setIsLoading(false);
        }
      } catch (err) {
        console.error("Redirect promise error:", err);
        setIsLoading(false);
      }
    };

    handleLoginComplete();
  }, []);

  const handleOpenSession = async (token: any) => {
    try {
      setStatusMessage("Creating your session...");
      const payload = {
        conURN: token.extension_UserURN,
        wURN: token.sub,
        name: token.given_name || token.name,
      };

      await initializeOpenSession(payload).then((res: any) => {
        console.log(res, "Session Opened!");
        setIsSessionOpen(true);

        // Clear login flow and redirect
        localStorage.removeItem("loginFlow");
        localStorage.removeItem("mode");
        const redirectUrl = localStorage.getItem("redirectUrl");
        localStorage.removeItem("redirectUrl");

        if (redirectUrl) {
          navigate(redirectUrl, { replace: true });
        } else {
          navigate("/paylink", { replace: true });
        }
      });
    } catch (err) {
      console.error("Session open failed:", err);
      setIsLoading(false);
    }
  };

  return (
    <section className="w-full min-h-screen bg-white flex items-center justify-center px-6">
      <div className="flex flex-col items-center gap-4 text-center">
        <CircularProgress size={28} />
        <p className="text-sm text-py_black-500">{statusMessage}</p>
        {!isLoading && (
          <p className="text-xs text-red-500">
            We were unable to complete sign in. Please try again.
          </p>
        )}
      </div>
    </section>
  );
};
